import React, { useState } from "react";
import { submitPilotApplication } from "../lib/api";
import { ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";

const ROLES = [
  "Artist",
  "Producer",
  "Engineer",
  "Songwriter",
  "DJ",
  "Manager",
  "A&R",
  "Tour Crew",
  "Videographer",
  "Photographer",
  "Session Musician",
  "Promoter",
  "Studio Owner",
  "Creative Director",
  "Other",
];

const REQUIRED_MSG = "Please complete all required fields.";
const SUCCESS_MSG =
  "You're on the Loudar waiting list. Check your inbox for a welcome email. We'll be in touch when there's news about access.";

export const WaitlistForm = ({ variant = "hero", onSuccess }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "",
    city: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.role || !form.city) {
      setStatus({ state: "error", msg: REQUIRED_MSG });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const res = await submitPilotApplication(form);
      setStatus({ state: "success", msg: SUCCESS_MSG });
      setForm({ name: "", email: "", role: "", city: "", message: "" });
      onSuccess && onSuccess(res);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again.";
      setStatus({ state: "error", msg: detail });
      // Note: we intentionally DO NOT clear the form on error so the user
      // keeps everything they entered.
    }
  };

  const isPilot = variant === "pilot";
  const req = <span className="ml-1 text-loudar-orange">*</span>;

  return (
    <form
      onSubmit={submit}
      data-testid={`waitlist-form-${variant}`}
      className={`w-full ${isPilot ? "space-y-4" : "space-y-3"}`}
      noValidate
    >
      <div className={`grid grid-cols-1 ${isPilot ? "md:grid-cols-2" : "md:grid-cols-2"} gap-3`}>
        <div>
          <label
            htmlFor={`wl-name-${variant}`}
            className="mb-1 block text-[10px] uppercase tracking-[0.22em] text-white/60"
          >
            Name{req}
          </label>
          <input
            id={`wl-name-${variant}`}
            type="text"
            placeholder="Your name"
            value={form.name}
            onChange={update("name")}
            data-testid={`waitlist-input-name-${variant}`}
            className="h-12 w-full rounded-sm border border-white/10 bg-white/[0.04] px-4 text-sm text-white placeholder:text-white/40 focus:border-loudar-orange focus:outline-none focus:ring-1 focus:ring-loudar-orange/50"
            required
            aria-required="true"
          />
        </div>
        <div>
          <label
            htmlFor={`wl-email-${variant}`}
            className="mb-1 block text-[10px] uppercase tracking-[0.22em] text-white/60"
          >
            Email{req}
          </label>
          <input
            id={`wl-email-${variant}`}
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={update("email")}
            data-testid={`waitlist-input-email-${variant}`}
            className="h-12 w-full rounded-sm border border-white/10 bg-white/[0.04] px-4 text-sm text-white placeholder:text-white/40 focus:border-loudar-orange focus:outline-none focus:ring-1 focus:ring-loudar-orange/50"
            required
            aria-required="true"
          />
        </div>
        <div>
          <label
            htmlFor={`wl-role-${variant}`}
            className="mb-1 block text-[10px] uppercase tracking-[0.22em] text-white/60"
          >
            Your role{req}
          </label>
          <select
            id={`wl-role-${variant}`}
            value={form.role}
            onChange={update("role")}
            data-testid={`waitlist-input-role-${variant}`}
            className="h-12 w-full appearance-none rounded-sm border border-white/10 bg-white/[0.04] px-4 text-sm text-white focus:border-loudar-orange focus:outline-none focus:ring-1 focus:ring-loudar-orange/50"
            required
            aria-required="true"
          >
            <option value="" className="bg-loudar-ink">
              Select your role
            </option>
            {ROLES.map((r) => (
              <option key={r} value={r} className="bg-loudar-ink">
                {r}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor={`wl-city-${variant}`}
            className="mb-1 block text-[10px] uppercase tracking-[0.22em] text-white/60"
          >
            City{req}
          </label>
          <input
            id={`wl-city-${variant}`}
            type="text"
            placeholder="City"
            value={form.city}
            onChange={update("city")}
            data-testid={`waitlist-input-city-${variant}`}
            className="h-12 w-full rounded-sm border border-white/10 bg-white/[0.04] px-4 text-sm text-white placeholder:text-white/40 focus:border-loudar-orange focus:outline-none focus:ring-1 focus:ring-loudar-orange/50"
            required
            aria-required="true"
          />
        </div>
      </div>

      {isPilot && (
        <div>
          <label
            htmlFor={`wl-message-${variant}`}
            className="mb-1 block text-[10px] uppercase tracking-[0.22em] text-white/60"
          >
            Tell us a bit about yourself (optional)
          </label>
          <textarea
            id={`wl-message-${variant}`}
            placeholder="Tell us a bit about yourself (optional)"
            rows={3}
            value={form.message}
            onChange={update("message")}
            data-testid={`waitlist-input-message-${variant}`}
            className="w-full rounded-sm border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-loudar-orange focus:outline-none focus:ring-1 focus:ring-loudar-orange/50"
          />
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status.state === "loading"}
          data-testid={`waitlist-submit-${variant}`}
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status.state === "loading" ? "Sending…" : "Join the waiting list"}
          <ArrowRight size={16} />
        </button>
        <p className="max-w-xs text-[11px] leading-relaxed text-white/45 sm:text-right">
          We'll use your details to contact you about Loudar and your place on the
          waiting list.
        </p>
      </div>

      {status.state === "success" && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-start gap-2 rounded-sm border border-loudar-orange/40 bg-loudar-orange/10 p-3 text-sm text-loudar-orange"
          data-testid={`waitlist-success-${variant}`}
        >
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>{status.msg}</span>
        </div>
      )}
      {status.state === "error" && (
        <div
          role="alert"
          aria-live="assertive"
          className="flex items-start gap-2 rounded-sm border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300"
          data-testid={`waitlist-error-${variant}`}
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{status.msg}</span>
        </div>
      )}
    </form>
  );
};

export default WaitlistForm;
